import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TeamContext, ITeamContext } from './Team.context'
import { Typography } from '@mui/material'
import tw from 'twin.macro'
import { TeamTabs } from './tabs/TeamTabs'
import { TeamTableTab } from 'src/constants/team/enums'
import { TeamMembersTable } from './tables/team-members-table/TeamMembersTable'
import {
  TeamMemberInvitesTable,
  TeamMemberInvitesTableRef,
} from './tables/team-member-invites-table/TeamMemberInvitesTable'
import { InviteTeamMemberButton } from './invite-team-member/InviteTeamMemberButton'
import { useDispatch, useSelector } from 'react-redux'
import { getPartnerId } from 'src/selectors/user'
import { ManageTeamRoles } from './manage-team-roles/ManageTeamRoles'
import { FetchTeamMemberInvitesCount, FetchTeamMembersCount } from 'src/constants/actionTypes'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'

const TeamComponent = () => {
  const dispatch = useDispatch()

  const partnerId = useSelector(getPartnerId)

  const { hasPermission } = useCheckPermissions()

  const selectedTabRef = useRef<TeamTableTab | null>(null)
  const teamMemberInvitesTableRef = useRef<TeamMemberInvitesTableRef>(null)

  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedTab, setSelectedTab] = useState<TeamTableTab | null>(null)

  const handleRefreshTeamMemberInvites = useCallback(() => {
    teamMemberInvitesTableRef.current?.refreshTeamMemberInvites()
  }, [])

  const teamContextValue = useMemo<ITeamContext>(() => {
    return {
      searchQuery,
      setSearchQuery,
    }
  }, [searchQuery, setSearchQuery])

  const handleSelectTab = useCallback((tab: TeamTableTab) => {
    selectedTabRef.current = tab
    setSelectedTab(tab)
  }, [])

  const fetchTeamMembersCount = useCallback(() => {
    if (!partnerId || !hasPermission(UserPermission.UsersView)) {
      return
    }

    dispatch(FetchTeamMembersCount.request())
  }, [partnerId, hasPermission])

  const fetchTeamMemberInvitesCount = useCallback(() => {
    if (!partnerId || !hasPermission(UserPermission.InvitesView)) {
      return
    }

    dispatch(FetchTeamMemberInvitesCount.request())
  }, [partnerId, hasPermission])

  useEffect(() => {
    if (!hasPermission(UserPermission.UsersView)) {
      return
    }

    fetchTeamMembersCount()
  }, [hasPermission])

  useEffect(() => {
    if (!hasPermission(UserPermission.InvitesView)) {
      return
    }

    fetchTeamMemberInvitesCount()
  }, [hasPermission])

  useEffect(() => {
    if (hasPermission(UserPermission.UsersView)) {
      setSelectedTab(TeamTableTab.Users)
    }

    if (hasPermission(UserPermission.InvitesView)) {
      setSelectedTab(TeamTableTab.Invites)
    }
  }, [hasPermission])

  return (
    <TeamContext.Provider value={teamContextValue}>
      <div css={tw`flex flex-row items-center justify-between mb-6`}>
        <Typography variant="h2" css={tw`mb-2`}>
          My Team
        </Typography>
        <div css={tw`flex`}>
          {hasPermission(UserPermission.RolesView) && <ManageTeamRoles />}
          {hasPermission(UserPermission.InvitesCreate) && (
            <InviteTeamMemberButton
              setSelectedTab={setSelectedTab}
              onRefreshTeamMemberInvites={handleRefreshTeamMemberInvites}
            />
          )}
        </div>
      </div>
      <TeamTabs selectedTab={selectedTab} onClick={handleSelectTab} />
      {selectedTab === TeamTableTab.Users && hasPermission(UserPermission.UsersView) && (
        <TeamMembersTable />
      )}
      {selectedTab === TeamTableTab.Invites && hasPermission(UserPermission.InvitesView) && (
        <TeamMemberInvitesTable ref={teamMemberInvitesTableRef} />
      )}
    </TeamContext.Provider>
  )
}

export const Team = memo(TeamComponent)
